import { useState, useEffect, useRef, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductSearch({ activeListId, onItemAdded }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newProduct, setNewProduct] = useState({ name: '', group_id: '', link: '', description: '', quantity: 1 });
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    api.get('/product-groups').then(({ data }) => {
      setGroups(data.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchProducts = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    try {
      const { data } = await api.get(`/products/search?q=${encodeURIComponent(q)}`);
      setResults(data);
    } catch { setResults([]); }
  }, []);

  const handleQueryChange = (value) => {
    setQuery(value);
    setShowResults(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchProducts(value), 250);
  };

  const handleSelectProduct = async (product) => {
    try {
      const { data } = await api.post(`/lists/${activeListId}/items`, {
        product_id: product.id, product_name: product.name,
        group_id: product.group_id || '', group_name: product.group_name,
        quantity: 1, description: product.description || '', link: product.link || ''
      });
      onItemAdded(data);
      setQuery('');
      setShowResults(false);
      toast.success(`${product.name} tilføjet`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name.trim()) return;
    setLoading(true);
    try {
      // Auto-assign "Øvrige" if no group selected
      let groupId = newProduct.group_id;
      if (!groupId) {
        const oevrige = groups.find(g => g.name === 'Øvrige');
        if (oevrige) groupId = oevrige.id;
        else if (groups.length > 0) groupId = groups[groups.length - 1].id;
      }
      if (!groupId) { toast.error('Ingen varegrupper tilgængelige'); setLoading(false); return; }
      const { data: product } = await api.post('/products', {
        name: newProduct.name, group_id: groupId,
        link: newProduct.link, description: newProduct.description
      });
      const { data: item } = await api.post(`/lists/${activeListId}/items`, {
        product_id: product.id, product_name: product.name,
        group_id: product.group_id || '', group_name: product.group_name,
        quantity: newProduct.quantity, description: newProduct.description, link: newProduct.link
      });
      onItemAdded(item);
      setShowCreate(false);
      setQuery('');
      setShowResults(false);
      setNewProduct({ name: '', group_id: '', link: '', description: '', quantity: 1 });
      toast.success(`${product.name} oprettet og tilfojet`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const openCreateDialog = () => {
    setNewProduct({ name: query, group_id: '', link: '', description: '', quantity: 1 });
    setShowCreate(true);
    setShowResults(false);
  };

  return (
    <>
      <div ref={wrapperRef} className="relative" data-testid="product-search">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C706A]" />
          <Input value={query} onChange={e => handleQueryChange(e.target.value)}
            onFocus={() => query && setShowResults(true)}
            placeholder="Søg eller tilføj vare..."
            className="pl-10 border-[#EAE6DF] bg-white focus:ring-[#7F9C82]/30 focus:border-[#7F9C82] font-sans"
            data-testid="add-product-input" />
        </div>
        {showResults && query.trim() && (
          <Card className="search-dropdown border-[#EAE6DF] shadow-lg mt-1 overflow-hidden" data-testid="search-results">
            {results.length > 0 ? (
              <>
                {results.map(product => (
                  <button key={product.id} onClick={() => handleSelectProduct(product)}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#7F9C82]/5 flex items-center gap-3 transition-colors border-b border-[#EAE6DF] last:border-0"
                    data-testid={`search-result-${product.id}`}>
                    <ShoppingBag className="w-4 h-4 text-[#7F9C82] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-sans text-sm font-medium text-[#2F3E3B]">{product.name}</span>
                      {product.group_name && <Badge variant="outline" className="ml-2 text-[10px] border-[#EAE6DF] text-[#5C706A]">{product.group_name}</Badge>}
                    </div>
                  </button>
                ))}
                <button onClick={openCreateDialog}
                  className="w-full px-4 py-2.5 text-left hover:bg-[#7F9C82]/5 flex items-center gap-3 text-[#7F9C82] font-sans text-sm border-t border-[#EAE6DF]"
                  data-testid="create-product-option">
                  <Plus className="w-4 h-4" />Opret "{query}" som ny vare
                </button>
              </>
            ) : (
              <button onClick={openCreateDialog}
                className="w-full px-4 py-3 text-left hover:bg-[#7F9C82]/5 flex items-center gap-3 text-[#7F9C82] font-sans text-sm"
                data-testid="create-product-no-results">
                <Plus className="w-4 h-4" />Opret "{query}" som ny vare
              </button>
            )}
          </Card>
        )}
      </div>
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white border-[#EAE6DF]">
          <DialogHeader><DialogTitle className="text-[#2F3E3B]">Opret ny vare</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Varenavn</Label>
              <Input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                className="border-[#EAE6DF]" data-testid="new-product-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Varegruppe</Label>
              <Select value={newProduct.group_id} onValueChange={v => setNewProduct({ ...newProduct, group_id: v })}>
                <SelectTrigger className="border-[#EAE6DF] font-sans" data-testid="new-product-group">
                  <SelectValue placeholder="Vælg varegruppe" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE6DF]">
                  {groups.map(g => <SelectItem key={g.id} value={g.id} className="font-sans">{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {groups.length === 0 && <p className="text-xs text-[#C87965] font-sans">Ingen varegrupper. En admin skal oprette grupper først.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Antal</Label>
                <Input type="number" min="1" value={newProduct.quantity}
                  onChange={e => setNewProduct({ ...newProduct, quantity: parseInt(e.target.value) || 1 })}
                  className="border-[#EAE6DF]" data-testid="new-product-quantity" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-sans text-[#2F3E3B]">Link (valgfrit)</Label>
                <Input value={newProduct.link} onChange={e => setNewProduct({ ...newProduct, link: e.target.value })}
                  placeholder="https://..." className="border-[#EAE6DF]" data-testid="new-product-link" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Beskrivelse (valgfrit)</Label>
              <Input value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder="F.eks. oekomærket, 1L..." className="border-[#EAE6DF]" data-testid="new-product-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="font-sans">Annuller</Button>
            <Button onClick={handleCreateProduct} disabled={loading || !newProduct.name.trim()}
              className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="create-product-button">
              {loading ? 'Opretter...' : 'Opret og tilføj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
